// Notifications — every notification sent to the signed-in person (owner, 2026-10-07 —
// docs/decisions.md D-310: "When a user clicks into the Alerts screen, they
// should see a listing of all alerts that have been sent to them. Clicking into
// one should reveal the same message that was sent to them via email."; Alerts
// was this screen's name until D-363).
//
// The list is the caller's own and nobody else's: the server reads by the
// caller's own address (`GET /notifications`), so this screen has no filter to get
// wrong. A row is the open control (`rowActivationProps`), as on every other
// list here, and it opens the message in a dialog.
//
// THE MESSAGE IS THE EMAIL ITSELF — the stored HTML, drawn in a sandboxed
// frame with no scripts, no forms and no same-origin access, so it reads
// exactly as it did in the inbox and can do nothing here. Every value in it
// was escaped when the email was rendered (`planUploadNotification`).
//
// THE SAME CHROME AS EVERY OTHER SCREEN (owner, 2026-10-07: "The bar at the
// top of the screen does not appear to be the same height as the same bar on
// all other screens" and "This screen needs Refresh and Filter buttons"). The
// header bar was 51px here and 63px everywhere else, because this was the one
// section with no page tab in it; it now shows its one tab, as Analytics does
// for a holder of one section, and carries Filter and Refresh in the header
// cluster, in that order, as Analytics does. The message opens "as large as
// the invoice modal" (same day) - the same `overlaySurface` geometry - and the
// email inside fills it (owner, the same day: "the content is really small,
// even though the modal is quite large"), though the sent email keeps its
// 640px cap. The column headers sort, as on practically every other table.

import { useCallback, useEffect, useMemo, useState } from 'react'
import {
  Body1,
  Button,
  DialogActions,
  DialogBody,
  DialogContent,
  DialogSurface,
  Spinner,
  Table,
  TableBody,
  TableCell,
  TableHeader,
  TableRow,
  Tooltip,
  makeStyles,
  mergeClasses,
  tokens,
} from '@fluentui/react-components'
import { ArrowClockwiseRegular } from '@fluentui/react-icons'
import { NOTIFICATION_TYPE, NOTIFICATION_TYPE_LABEL, type NotificationType } from '@ap/shared'
import { AppDialog } from '../components/AppDialog'
import { FrozenWhileClosed } from '../components/FrozenWhileClosed'
import { ListFilters } from '../components/ListFilters'
import { OVERLAY_BODY_STYLE, OVERLAY_SURFACE_STYLE, useOverlayGeometry } from '../components/overlaySurface'
import { ScreenActions } from '../components/ScreenActions'
import { ScreenIntro } from '../components/ScreenIntro'
import { SortableHeaderCell } from '../components/SortableHeaderCell'
import { ErrorBanner } from '../components/States'
import { rowActivationProps, useClickableRowClass, useRoomyTableClass } from '../components/tableDensity'
import { errorMessage } from '../api/errors'
import { BRAND_FLIP, BRAND_PATHS, BRAND_VIEWBOX } from '../lib/brand'
import { loadNotification, loadNotifications } from '../lib/data'
import { formatDateTime } from '../lib/format'
import { sortRows, useTableSort } from '../lib/tableSort'
import type { Notification, NotificationSummary } from '../lib/types'

const useStyles = makeStyles({
  table: { tableLayout: 'fixed', width: '100%' },
  none: { display: 'block', padding: `${tokens.spacingVerticalM} 0`, color: tokens.colorNeutralForeground3 },
  // As large as the invoice modal (owner, 2026-10-07). The frame fills the
  // dialog and scrolls, and the email fills the frame (`forViewing`).
  surface: OVERLAY_SURFACE_STYLE,
  body: OVERLAY_BODY_STYLE,
  content: { display: 'flex', flexDirection: 'column', minHeight: 0 },
  frame: {
    display: 'block',
    flex: 1,
    width: '100%',
    // Border inside the width, or the 1px borders overflow the dialog's
    // content box by 2px and give it a horizontal scrollbar.
    boxSizing: 'border-box',
    minHeight: '240px',
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    borderRadius: tokens.borderRadiusMedium,
    backgroundColor: '#f5f5f5',
  },
})

/**
 * The Subject column without the emails' shared "Invoice Processing: " prefix
 * (owner, 2026-10-19: "Let's drop the "Invoice Processing: " prefix in the
 * Subject column's values."). Every subject has it, so on this screen it says
 * nothing; the stored subject, and the email, keep it.
 */
const SUBJECT_PREFIX = 'Invoice Processing: '
const listSubject = (subject: string): string =>
  subject.startsWith(SUBJECT_PREFIX) ? subject.slice(SUBJECT_PREFIX.length) : subject

export function NotificationsScreen() {
  const s = useStyles()
  const roomy = useRoomyTableClass()
  const clickable = useClickableRowClass()
  const [notifications, setNotifications] = useState<NotificationSummary[] | null>(null)
  const [error, setError] = useState('')
  const [openId, setOpenId] = useState<string | null>(null)
  // The Filter popover's three controls; `''` and an empty pick do not narrow.
  const [term, setTerm] = useState('')
  const [notificationFilter, setNotificationFilter] = useState<string[]>([])
  const [companyFilter, setCompanyFilter] = useState<string[]>([])
  // Newest first until a header is clicked. `sent_at` is ISO, so it sorts as text.
  const sort = useTableSort<'sent' | 'notification' | 'subject'>({ key: 'sent', direction: 'desc' })

  const load = useCallback(async () => {
    try {
      setNotifications(await loadNotifications())
      setError('')
    } catch (e) {
      setNotifications([])
      setError(errorMessage(e))
    }
  }, [])

  useEffect(() => {
    void load()
  }, [load])

  // The dropdowns offer what the list holds: every notification type there is, and
  // the companies these notifications are about, A-Z.
  const notificationOptions = (Object.values(NOTIFICATION_TYPE) as NotificationType[]).map((t) => ({ value: t, label: NOTIFICATION_TYPE_LABEL[t] }))
  const companyOptions = useMemo(
    () =>
      [...new Set((notifications ?? []).map((a) => a.company_name).filter((c): c is string => c !== null))]
        .sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' }))
        .map((c) => ({ value: c, label: c })),
    [notifications],
  )
  const visible = useMemo(() => {
    const q = term.trim().toLowerCase()
    return (notifications ?? []).filter(
      (a) =>
        (notificationFilter.length === 0 || notificationFilter.includes(a.notification_type)) &&
        (companyFilter.length === 0 || companyFilter.includes(a.company_name ?? '')) &&
        (q === '' ||
          listSubject(a.subject).toLowerCase().includes(q) ||
          NOTIFICATION_TYPE_LABEL[a.notification_type].toLowerCase().includes(q) ||
          (a.company_name ?? '').toLowerCase().includes(q)),
    )
  }, [notifications, term, notificationFilter, companyFilter])
  const sorted = useMemo(
    () =>
      sortRows(visible, sort.state, {
        sent: (a) => a.sent_at,
        notification: (a) => NOTIFICATION_TYPE_LABEL[a.notification_type],
        subject: (a) => listSubject(a.subject),
      }),
    [visible, sort.state],
  )

  return (
    <div>
      {/* No page tab (owner, 2026-10-19: "Since there are no tabs other than
          "All" inside this Notifications screen, let's just drop the "All"
          tab."). The header keeps its height on its own (`topbar`, App.tsx). */}
      <ScreenActions>
        <ListFilters
          term={term}
          onTermChange={setTerm}
          searchPlaceholder="Subject or company…"
          ariaLabel="Notifications"
          selects={[
            { multiple: true, label: 'Notifications', values: notificationFilter, onChange: setNotificationFilter, options: notificationOptions },
            {
              multiple: true,
              label: 'Companies',
              values: companyFilter,
              onChange: setCompanyFilter,
              options: companyOptions,
            },
          ]}
        />
        <Tooltip content="Refresh" relationship="label">
          <Button appearance="secondary" icon={<ArrowClockwiseRegular />} onClick={() => void load()} />
        </Tooltip>
      </ScreenActions>
      <ScreenIntro>Review the notifications that were emailed you.</ScreenIntro>
      <ErrorBanner message={error} />
      {notifications === null ? (
        <Spinner label="Loading your notifications…" />
      ) : notifications.length === 0 ? (
        error === '' && <Body1 className={s.none}>No notifications have been sent to you.</Body1>
      ) : visible.length === 0 ? (
        <Body1 className={s.none}>Nothing matches the current filters.</Body1>
      ) : (
        <Table size="small" aria-label="Your notifications" className={mergeClasses(roomy, s.table)}>
          <TableHeader>
            <TableRow>
              <SortableHeaderCell style={{ width: '200px' }} sortDirection={sort.directionFor('sent')} onSort={() => sort.toggle('sent')}>
                Sent
              </SortableHeaderCell>
              <SortableHeaderCell style={{ width: '140px' }} sortDirection={sort.directionFor('notification')} onSort={() => sort.toggle('notification')}>
                Notification
              </SortableHeaderCell>
              <SortableHeaderCell sortDirection={sort.directionFor('subject')} onSort={() => sort.toggle('subject')}>
                Subject
              </SortableHeaderCell>
            </TableRow>
          </TableHeader>
          <TableBody>
            {sorted.map((a) => (
              <TableRow key={a.id} className={clickable} {...rowActivationProps(() => setOpenId(a.id), `Open: ${listSubject(a.subject)}`)}>
                <TableCell>{formatDateTime(a.sent_at)}</TableCell>
                <TableCell>{NOTIFICATION_TYPE_LABEL[a.notification_type]}</TableCell>
                <TableCell>{listSubject(a.subject)}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
      <NotificationDialog id={openId} onClose={() => setOpenId(null)} />
    </div>
  )
}

/** One notification, read when it opens: the email as it was sent. */
function NotificationDialog({ id, onClose }: { id: string | null; onClose: () => void }) {
  const s = useStyles()
  const [notification, setNotification] = useState<Notification | null>(null)
  const [error, setError] = useState('')
  useOverlayGeometry(id !== null)

  useEffect(() => {
    if (id === null) return
    let live = true
    setNotification(null)
    setError('')
    loadNotification(id).then(
      (a) => {
        if (live) setNotification(a)
      },
      (e: unknown) => {
        if (live) setError(errorMessage(e))
      },
    )
    return () => {
      live = false
    }
  }, [id])

  return (
    <AppDialog open={id !== null} onOpenChange={(_, d) => !d.open && onClose()}>
      {/* NO TITLE ABOVE THE EMAIL (owner, 2026-10-07 — D-319: "I still see
          "New Upload" at the top of the pop-up box above the email
          message rendering"). The email carries its own heading; the notification's
          name stays the dialog's accessible name. */}
      <DialogSurface className={s.surface} aria-label={notification === null ? 'Notification' : NOTIFICATION_TYPE_LABEL[notification.notification_type]}>
        <FrozenWhileClosed open={id !== null}>
          <DialogBody className={s.body}>
            <DialogContent className={s.content}>
              <ErrorBanner message={error} />
              {notification === null ? (
                error === '' && <Spinner label="Opening the notification…" />
              ) : (
                <iframe
                  title={NOTIFICATION_TYPE_LABEL[notification.notification_type]}
                  className={s.frame}
                  // No scripts, no forms, no same-origin access: the email is
                  // shown, never run. A link in it opens in a new tab.
                  sandbox="allow-popups allow-popups-to-escape-sandbox"
                  srcDoc={forViewing(notification.body_html)}
                />
              )}
            </DialogContent>
            <DialogActions>
              <Button appearance="secondary" onClick={onClose}>
                Close
              </Button>
            </DialogActions>
          </DialogBody>
        </FrozenWhileClosed>
      </DialogSurface>
    </AppDialog>
  )
}

/**
 * The email, ready for the frame: it fills the frame rather than stopping at
 * the 640px an inbox gets, a link opens in a new tab, never inside the frame,
 * and the logo the email carries as an inline attachment (`cid:`) is
 * drawn from the app's own artwork, since the stored copy has no attachment
 * for the frame to resolve.
 */
function forViewing(html: string): string {
  return html
    .replace(/<head>/i, '<head><base target="_blank">')
    .replace(/cid:ap-brand-mark/g, BRAND_MARK_DATA_URI)
    .replace(/width="640"/g, 'width="100%"')
    .replace(/max-width:640px/g, 'max-width:none')
}

/** The butterfly mark as an SVG `data:` URI, from the same paths `BrandMark.tsx` draws. */
const BRAND_MARK_DATA_URI = `data:image/svg+xml;charset=utf-8,${encodeURIComponent(
  `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${BRAND_VIEWBOX} ${BRAND_VIEWBOX}">` +
    BRAND_PATHS.map((p) => `<path transform="${BRAND_FLIP}" fill="${p.fill}" d="${p.d}"/>`).join('') +
    '</svg>',
)}`
